import type { ContractTerms } from '@inflip/contracts/user';
import {
  usageScopeRows,
  proposalPriceRows,
  BRAND_ASSET_LABELS,
  USAGE_PLACEMENT_LABELS,
  BRAND_LEGAL_USE_LABELS,
  USAGE_REGION_LABELS,
  EXCLUSIVITY_LABELS,
  MODIFICATION_RIGHT_LABELS,
  VIOLATION_POLICY_LABELS,
  PRICING_MODEL_LABELS,
  forbiddenIndustryLabel,
  numberedNoteText,
} from '@inflip/shared';

import { InfoRow, Section } from './ui';

/** Display the accepted snapshot, never today's listing conditions. */
export function orderContractRows(terms: ContractTerms) {
  return [
    ...(terms.proposalPricing
      ? proposalPriceRows(terms.proposalPricing).map((row) =>
          row.label === '합의 결제 금액'
            ? { ...row, value: `${terms.upfrontAmount.toLocaleString('ko-KR')}원` }
            : row,
        )
      : [{ label: '계약 금액', value: `${terms.upfrontAmount.toLocaleString('ko-KR')}원` }]),
    ...(terms.proposalPricing &&
    terms.rightType === 'CO_BRANDING' &&
    terms.proposalPricing.campaignCount === 1
      ? [{ label: '캠페인 수', value: '1건 (기본 사용료와 추가금에 적용)' }]
      : []),
    { label: '가격 방식', value: PRICING_MODEL_LABELS[terms.pricingModel] },
    { label: '이용 기간', value: terms.durationMonths ? `${terms.durationMonths}개월` : '1회' },
    { label: '사용 지역', value: USAGE_REGION_LABELS[terms.region] },
    { label: '독점 여부', value: EXCLUSIVITY_LABELS[terms.exclusivity] },
    {
      label: '수정 가능 범위',
      value:
        terms.modificationRights.map((key) => MODIFICATION_RIGHT_LABELS[key]).join(', ') || '없음',
    },
    { label: '사전 승인', value: terms.requiresPreApproval ? '필요' : '불필요' },
    {
      label: '금지 업종',
      value: terms.forbiddenIndustries.map((v) => forbiddenIndustryLabel(v)).join(', ') || '없음',
    },
    {
      label: '위반 시 조치',
      value:
        terms.violationPolicies.map((key) => VIOLATION_POLICY_LABELS[key]).join(', ') ||
        '별도 협의',
    },
    ...(terms.royaltyRateBp
      ? [
          {
            label: '매출 로열티',
            value: `${terms.royaltyRateBp / 100}%${terms.royaltyPaymentDay ? ` · 매달 ${terms.royaltyPaymentDay}일 지급` : ' · 지급일 기록 없음'}`,
          },
        ]
      : []),
    ...(terms.brandScope
      ? [
          {
            label: '제공 표기·자산',
            value:
              terms.brandScope.assets.map((o) => BRAND_ASSET_LABELS[o.key]).join(', ') || '미제공',
          },
          {
            label: '사용 위치',
            value:
              terms.brandScope.placements.map((o) => USAGE_PLACEMENT_LABELS[o.key]).join(', ') ||
              '미제공',
          },
          {
            label: '상품명·상표 사용',
            value:
              terms.brandScope.legalUses.map((o) => BRAND_LEGAL_USE_LABELS[o.key]).join(', ') ||
              '미제공',
          },
        ]
      : []),
    ...(terms.usageScope ? usageScopeRows(terms.usageScope) : []),
    ...(terms.usageNotes
      ? [{ label: '판매자 주의·규칙', value: numberedNoteText(terms.usageNotes) }]
      : []),
  ];
}

export function OrderContractTerms({ terms }: { terms: ContractTerms }) {
  const rows = orderContractRows(terms);
  return (
    <Section title="합의된 계약 조건" divided>
      {rows.map((row, i) => (
        <InfoRow key={`${i}-${row.label}`} label={row.label}>
          <span className="whitespace-pre-wrap">{row.value}</span>
        </InfoRow>
      ))}
    </Section>
  );
}
